/*
 * This file is part of the project by AGBOKOUDJO Franck.
 *
 * (c) AGBOKOUDJO Franck <internationaleswebservices@gmail.com>
 * Phone: +229 0167 25 18 86
 * LinkedIn: https://www.linkedin.com/in/internationales-web-services-120520193/
 * Github: https://github.com/Agbokoudjo/sonata_spa
 * Company: INTERNATIONALES WEB APPS & SERVICES
 *
 * For more information, please feel free to contact the author.
 */

// src/Session/SessionRecoveryManager.ts

import Swal from 'sweetalert2';
import type { EventSubscriberInterface } from '@wlindabla/event_dispatcher';
import {
    FetchRequestErrorEvent,
    HttpClientEvents,
} from '@wlindabla/http_client/events';
import { HttpRedirectResponseError } from '@wlindabla/http_client/exceptions';

import { SpaEvents, SpaRequestEvent, SpaFormSubmitEvent } from '../Events';
import { FormBindingManager } from '../Binding/FormBindingManager';
import { SonataSpaLogger } from '../Logger';
import type { SessionRecoveryOptions, SessionRecoveryMessages } from '../types';
import { safeFetch } from '@wlindabla/http_client/core';

type PendingAction =
    | { type: 'page'; url: string }
    | { type: 'form'; form: HTMLFormElement };

const DEFAULT_MESSAGES: Required<SessionRecoveryMessages> = {
    sessionExpiredTitle: 'Server-side session expired',
    sessionExpiredText: 'Confirm your password to continue.',
    passwordInputLabel: 'Password',
    passwordInputPlaceholder: 'Your password',
    confirmButtonText: 'Confirm',
    cancelButtonText: 'Cancel',
    passwordRequiredText: 'The password is required.',
    reconnectionFailedTitle: 'Reconnection failed',
    reconnectionFailedText: 'Incorrect password or session not restored.',
    networkErrorTitle: 'Network error',
    networkErrorText: 'Unable to contact the server. Please try again.',
    sessionRestoredTitle: 'Session restored',
    sessionRestoredFormText: 'Please click Send again.',
};

/**
 * Optional, opt-in alternative to a hard redirect on session expiry.
 *
 * Requires a Symfony `json_login` checker configured server-side (see
 * https://symfony.com/doc/current/security.html#json-login and the
 * "Session Recovery" section of the sonata_spa docs). When
 * `SpaRouterOptions.sessionRecovery` is NOT provided, SpaKernel falls back
 * to a plain `HttpRedirectSubscriber` (immediate redirect/navigate — no
 * modal, no resume) — see SpaKernel.registerBuiltInSubscribers().
 *
 * Flow:
 *   1. Every navigation (SpaEvents.REQUEST) and form submit
 *      (SpaEvents.FORM_SUBMIT) is remembered as "the last thing the user
 *      was doing" — nothing else, no side effects at this point.
 *   2. On HttpRedirectResponseError:
 *        - not an auth-like redirect → delegate to `onOtherRedirect`
 *          (same responsibility as the plain HttpRedirectSubscriber would
 *          have had)
 *        - looks like an auth redirect → prompt for the password via
 *          SweetAlert2, POST it to `loginCheckPath`, then resume:
 *            - page navigation → re-fetch the remembered URL automatically
 *              (no side effects, safe to retry silently)
 *            - form submission → only re-enable the submit button(s) and
 *              let the user click "Submit" again themselves (a form POST
 *              has side effects — never resubmitted automatically)
 */
export class SessionRecoveryManager implements EventSubscriberInterface {

    private pending: PendingAction | null = null;
    private readonly messages: Required<SessionRecoveryMessages>;

    public constructor(
        private readonly options: SessionRecoveryOptions,
        private readonly navigate: (url: string) => Promise<void>,
        private readonly onOtherRedirect: (error: HttpRedirectResponseError) => void
    ) {
        this.messages = { ...DEFAULT_MESSAGES, ...(options.messages ?? {}) };
    }

    public getSubscribedEvents(): Record<string, string | { listener: string; priority?: number }> {
        return {
            [SpaEvents.REQUEST]: { listener: 'onRequest', priority: 100 },
            [SpaEvents.FORM_SUBMIT]: { listener: 'onFormSubmit', priority: 100 },
            [HttpClientEvents.ERROR]: { listener: 'onRequestError', priority: 20 },
        };
    }

    public onRequest(event: SpaRequestEvent): void {
        this.pending = { type: 'page', url: event.request.url };
    }

    public onFormSubmit(event: SpaFormSubmitEvent): void {
        this.pending = { type: 'form', form: event.form };
    }

    public async onRequestError(event: FetchRequestErrorEvent): Promise<void> {
        if (event.isPropagationStopped() || event.isDefaultPrevented()) {
            return;
        }

        const error = event.getError();
        if (!(error instanceof HttpRedirectResponseError)) {
            return;
        }

        if (!error.looksLikeAuthRedirect()) {
            this.onOtherRedirect(error);
            return;
        }

        await this.promptForPassword();
    }

    private async promptForPassword(): Promise<void> {
        const username = this.options.getUsername();
        const m = this.messages;

        const { value: password } = await Swal.fire({
            title: m.sessionExpiredTitle,
            text: m.sessionExpiredText,
            input: 'password',
            inputLabel: m.passwordInputLabel,
            inputPlaceholder: m.passwordInputPlaceholder,
            inputAttributes: {
                autocapitalize: 'off',
                maxlength: "255",
                minlength: "8",
                autocorrect: 'off'
            },
            showCancelButton: true,
            confirmButtonText: m.confirmButtonText,
            cancelButtonText: m.cancelButtonText,
            inputValidator: (value) => (!value ? m.passwordRequiredText : undefined),
        });

        if (!password) {
            return; // user cancelled — nothing resumed, no forced fallback
        }

        try {
            const usernameField = this.options.usernameField ?? 'username';
            const passwordField = this.options.passwordField ?? 'password';

            const response = await safeFetch({
                url: this.options.loginCheckPath,
                methodSend: "POST",
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json' },
                data: {
                    [usernameField]: username,
                    [passwordField]: password
                }
            });

            if (!response.succeeded) {
                const _data = response.data as Record<string, string> | null;
                await Swal.fire({
                    icon: 'error',
                    title: _data?.title ?? _data?.errorTitle ?? m.reconnectionFailedTitle,
                    text: _data?.message ?? _data?.errorMessage ?? m.reconnectionFailedText,
                });
                return this.promptForPassword();
            }

            await this.resumePendingAction();
        } catch (networkError) {
            SonataSpaLogger.error('[SessionRecoveryManager] login_check failed:', networkError);
            await Swal.fire({
                icon: 'error',
                title: m.networkErrorTitle,
                text: m.networkErrorText,
            });
        }
    }

    private async resumePendingAction(): Promise<void> {
        const m = this.messages;

        if (!this.pending) {
            await Swal.fire({ icon: 'success', title: m.sessionRestoredTitle });
            return;
        }

        if (this.pending.type === 'page') {
            // No side effects on a GET navigation — safe to retry automatically.
            await this.navigate(this.pending.url);
            return;
        }

        // Form submission: re-enable the submit button(s) but do NOT
        // resubmit automatically — a POST has side effects, the user
        // decides when to retry.
        const bindingManager = FormBindingManager.getInstance();
        bindingManager?.updateSubmitButtonState(this.pending.form, true);

        await Swal.fire({
            icon: 'success',
            title: m.sessionRestoredTitle,
            text: m.sessionRestoredFormText,
        });
    }
}